import { MODAL_NONE } from '@components/App/Modal/constants'
import { closeTopEscapeLayer, hasOpenRadixMenu } from '@lib/escape'
import { artBoardActions } from '@store/reducers/artboard'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import {
  CanvasActionTypes,
  clearAttachmentsSelection,
} from '@storeOld/reducers/canvas/canvas'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import type { AppState } from '@/types'
import { Keys } from '../keyboard'

const useOnEscapeKeyboardActions = () => {
  const dispatch = useDispatch()

  // node selectors
  const selectedNode = useSelector<AppState, string | null>(
    ({ canvas }) =>
      canvas.node.selectedNodes[canvas.node.selectedNodes.length - 1]
  )

  const selectedToEditNode = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.node.selectedToEditNode
  )

  // block selectors
  const selectedBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.ids[canvas.block.ids.length - 1]
  )

  const selectedToEditBlock = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.block.selectedToEditBlockId
  )

  // symbol selectors
  const selectedSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedSymbol
  )

  const selectedToEditSymbol = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol
  )

  // external link selectors
  const selectedExternalLink = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.externalLink.selectedExternalLink
  )

  const isSearchVisible = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.showStructure
  )

  const isImagePreviewVisible = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.preview !== null
  )

  const cursorMode = useSelector<AppState, ArtBoardCursorMode>(
    ({ artboard }) => artboard.cursorMode
  )

  const modalType = useSelector<AppState, string>(({ ui }) => ui.modalType)

  const {
    arrows,
    dropzone,
    activityHistory,
    color,
    settings,
    storage,
    estimate,
    symbolLibrary,
    gallery,
    edit,
  } = useArtboardMode()

  // Esc в режиме стрелок отменяет создание — ровно как клик по пустому месту
  // (OCT-5140).
  const escapeToEdit =
    arrows ||
    symbolLibrary ||
    settings ||
    color ||
    activityHistory ||
    storage ||
    estimate

  const escapeCase =
    arrows ||
    symbolLibrary ||
    settings ||
    color ||
    activityHistory ||
    storage ||
    estimate ||
    dropzone

  const onEscapeNode = () => {
    if (!selectedToEditNode) {
      dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_NODES })

      return
    }

    dispatch({
      type: CanvasActionTypes.SELECT_NODES,
      payload: [selectedNode],
    })
  }

  const onEscapeBlock = () => {
    if (!selectedToEditBlock) {
      dispatch({
        type: CanvasActionTypes.CLEAR_SELECTED_BLOCKS,
      })

      return
    }

    dispatch({
      type: CanvasActionTypes.SELECT_BLOCK,
      payload: selectedBlock,
    })
  }

  const onEscapeSymbol = () => {
    if (!selectedToEditSymbol) {
      dispatch({
        type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL,
      })

      return
    }

    dispatch({
      type: CanvasActionTypes.SELECT_SYMBOL,
      payload: selectedSymbol,
    })
  }

  const onEscapeExternalLink = () => {
    dispatch({
      type: CanvasActionTypes.CLEAR_SELECTED_EXTERNAL_LINK,
    })
  }

  const onEscapeSearch = () => {
    dispatch(artBoardActions.showStructure({ showStructure: false }))
  }

  const onClearAttachmentsSelection = () => {
    // @todo implement action
    dispatch(clearAttachmentsSelection())
  }

  const onEscapeImagePreview = () => {
    dispatch(artBoardActions.setPreview(null))
  }

  const onEscapeArtboardMode = () => {
    if (symbolLibrary && !selectedSymbol && !selectedToEditSymbol) {
      return
    }

    if (!escapeToEdit) {
      return
    }

    dispatch(artBoardActions.changeMode('edit'))
  }

  return (event: KeyboardEvent) => {
    if (event.code !== Keys.Escape) {
      return
    }

    // A component that already handled this Escape claims it with
    // event.preventDefault(); a Radix menu and a modal handle it with
    // document-level listeners of their own that cannot mark the event.
    if (event.defaultPrevented || hasOpenRadixMenu()) {
      return
    }

    if (modalType !== MODAL_NONE) {
      return
    }

    // Layered UI (panels, dropdowns, threads, comment mode) closes one
    // layer per press, most recently opened first.
    if (closeTopEscapeLayer()) {
      return
    }

    const hasNodeSelection = Boolean(selectedNode || selectedToEditNode)
    const hasBlockSelection = Boolean(selectedBlock || selectedToEditBlock)
    const hasSymbolSelection = Boolean(selectedSymbol || selectedToEditSymbol)
    const hasImagePreview = gallery && isImagePreviewVisible
    const hasModeToEscape =
      escapeToEdit &&
      !(symbolLibrary && !selectedSymbol && !selectedToEditSymbol)

    if (hasNodeSelection) {
      onEscapeNode()
    }

    if (hasBlockSelection) {
      onEscapeBlock()
    }

    if (hasSymbolSelection) {
      onEscapeSymbol()
    }

    if (selectedExternalLink) {
      onEscapeExternalLink()
    }

    if (isSearchVisible) {
      onEscapeSearch()
    }

    if (hasImagePreview) {
      onEscapeImagePreview()
    }

    onClearAttachmentsSelection()

    if (escapeCase) {
      onEscapeArtboardMode()
    }

    const escaped =
      hasNodeSelection ||
      hasBlockSelection ||
      hasSymbolSelection ||
      Boolean(selectedExternalLink) ||
      isSearchVisible ||
      hasImagePreview ||
      hasModeToEscape

    // Select pages is the outermost layer: it resets to the default tool
    // only when the press had nothing else left to close.
    if (!escaped && edit && cursorMode === 'select') {
      dispatch(artBoardActions.setCursorMode('grab'))
    }
  }
}

export default useOnEscapeKeyboardActions
